import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2, XCircle, Undo2, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import CancellationFlowModal from '../components/CancellationFlowModal';
import { getMySubscription, reactivateSubscription, createBillingPortalSession } from '../services/subscriptions';
import { formatDate } from '../utils/currencyFormatter';

const TIER_LABELS = { trial: 'Prueba', entrepreneur: 'Emprendedor', pro: 'Pro', elite: 'Elite' };

const Settings = () => {
  const { user, loading, forgotPassword, refreshSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [sendingReset, setSendingReset] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subActionLoading, setSubActionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showCancellationFlow, setShowCancellationFlow] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  const loadSubscription = useCallback(async () => {
    setSubLoading(true);
    try {
      const data = await getMySubscription();
      setSubscription(data);
    } catch {
      toast.error('No se pudo cargar la información de tu suscripción');
    } finally {
      setSubLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadSubscription();
    }
  }, [user, loadSubscription]);

  const handleCancellationFlowResolved = async () => {
    await loadSubscription();
    await refreshSubscriptionStatus?.();
  };

  const handleReactivateSubscription = async () => {
    setSubActionLoading(true);
    try {
      await reactivateSubscription();
      await loadSubscription();
      toast.success('Tu suscripción fue reactivada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reactivar la suscripción');
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await createBillingPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo abrir el portal de pagos');
      setPortalLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    try {
      await forgotPassword(user.email);
      toast.success('Te enviamos un correo para restablecer tu contraseña');
    } catch {
      toast.error('Error al enviar el correo de restablecimiento');
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-settings"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold font-outfit text-foreground">Configuración</h1>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-2xl">
          <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8">
            <h3 className="text-lg font-semibold font-outfit mb-4">Información de la Cuenta</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Nombre</span>
                <span className="text-foreground font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Email</span>
                <span className="text-foreground font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Sitio de Mercado Libre</span>
                <span className="text-foreground font-medium">{user?.mercado_libre_site}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">ID de Usuario MeLi</span>
                <span className="text-foreground font-medium">{user?.mercado_libre_user_id}</span>
              </div>
            </div>
            <div className="pt-6 mt-2">
              <Button
                onClick={handleResetPassword}
                disabled={sendingReset}
                variant="outline"
                className="border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300"
              >
                {sendingReset ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                Reestablecer contraseña
              </Button>
              <p className="text-xs text-muted-foreground/70 mt-2">
                Se enviará un correo a {user?.email} con instrucciones para restablecer tu contraseña.
              </p>
            </div>
          </div>

          <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8 mt-6">
            <h3 className="text-lg font-semibold font-outfit mb-4">Suscripción</h3>

            {subLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Cargando...
              </div>
            ) : !subscription || subscription.status === 'none' || !subscription.is_active ? (
              <div className="space-y-3">
                <p className="text-muted-foreground">No tienes un plan activo actualmente.</p>
                <Button onClick={() => navigate('/select-plan')}>Ver planes</Button>
              </div>
            ) : subscription.cancel_at_period_end ? (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Plan actual</span>
                  <span className="text-foreground font-medium">
                    {TIER_LABELS[subscription.tier] || subscription.tier}
                  </span>
                </div>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Tu suscripción se cancelará el{' '}
                  {subscription.current_period_end
                    ? formatDate(subscription.current_period_end, user?.mercado_libre_site)
                    : 'fin del período actual'}
                  . No se te realizará ningún cobro adicional y mantendrás acceso hasta esa fecha.
                </p>
                <Button
                  onClick={handleReactivateSubscription}
                  disabled={subActionLoading}
                  variant="outline"
                  className="border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  {subActionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Undo2 className="w-4 h-4 mr-2" />
                  )}
                  Mantener mi suscripción
                </Button>
                {subscription.stripe_customer_id && (
                  <div className="pt-2">
                    <Button
                      onClick={handleUpdatePaymentMethod}
                      disabled={portalLoading}
                      variant="outline"
                    >
                      {portalLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Actualizar método de pago
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {subscription.status === 'past_due' && (
                  <div className="flex items-start gap-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300">
                      No pudimos cobrar tu última renovación. Actualiza tu método de pago para evitar
                      perder el acceso a la plataforma.
                    </p>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Plan actual</span>
                  <span className="text-foreground font-medium">
                    {TIER_LABELS[subscription.tier] || subscription.tier}
                  </span>
                </div>
                {subscription.current_period_end && (
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Próxima renovación</span>
                    <span className="text-foreground font-medium">
                      {formatDate(subscription.current_period_end, user?.mercado_libre_site)}
                    </span>
                  </div>
                )}
                {subscription.stripe_customer_id && (
                  <div className="pt-4">
                    <Button
                      onClick={handleUpdatePaymentMethod}
                      disabled={portalLoading}
                      variant={subscription.status === 'past_due' ? 'default' : 'outline'}
                    >
                      {portalLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Actualizar método de pago
                    </Button>
                  </div>
                )}
                <div className="pt-4">
                  <Button
                    onClick={() => setShowCancellationFlow(true)}
                    disabled={subActionLoading}
                    variant="outline"
                    className="border-red-600 text-red-600 dark:text-red-400 hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar suscripción
                  </Button>
                  <CancellationFlowModal
                    open={showCancellationFlow}
                    onOpenChange={setShowCancellationFlow}
                    tier={subscription.tier}
                    onResolved={handleCancellationFlowResolved}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
